import React, { useState, ReactNode, useEffect, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@core/db'; // temporal: se irá reduciendo uso directo
import { projectsRepo } from '@core/db/repositories/projectsRepo';
import { sectionsRepo } from '@core/db/repositories/sectionsRepo';
import { sectionContentsRepo } from '@core/db/repositories/sectionContentsRepo';
import { writingVersionsRepo } from '@core/db/repositories/writingVersionsRepo';
import {
  Genre,
  PendingProjectResumeRequest,
  Section,
  SectionContent,
  SectionPlanningState,
  SectionStatus,
  WriterContextType,
} from '../types';
import { v4 as uuidv4 } from 'uuid';
import { genres } from '../constants';
import { outlineOpsRepo } from '@core/db/repositories/outlineOpsRepo';
import { computeOutlineReorder, OutlineReorderAction } from '@shared/utils/outlineReorder';
import { WriterContext } from './writerContext';
import { resolveNextActiveVersionId } from '@/features/editor/ui/writingVersionUtils';
import {
  PROJECT_RESUME_CHECKPOINT_KEY,
  mergeCustomFieldsWithResumeCheckpoint,
  sanitizeProjectResumeCheckpoint,
} from '@/features/resume/projectResumeState';
import {
  decodeRemoteResumeSectionContent,
  upsertRemoteResumeSectionContent,
} from '@/features/resume/resumePrefetch';

// Evitar mezclar import estático y dinámico de @core/sync en el bundle
async function getDeviceIdLazy(): Promise<string> {
  const { getDeviceId } = await import('@core/sync');
  return getDeviceId();
}

const LOCAL_SAVE_DEBOUNCE_MS = 450;
const LOCAL_SAVE_THROTTLE_MS = 2500;

export const WriterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedGenre, setSelectedGenre] = useState<Genre | null>(null);
  const [activeProjectId, setActiveProjectIdState] = useState<string | undefined>();
  const [pendingProjectResume, setPendingProjectResume] = useState<PendingProjectResumeRequest | null>(null);
  const [isResumeFetching, setIsResumeFetching] = useState(false);
  const [activeSection, setActiveSection] = useState<Section | undefined>();
  const [activeVersionId, setActiveVersionId] = useState<string | undefined>();
  const [editorContent, setEditorContentState] = useState<string>('');
  const [isSectionContentReady, setIsSectionContentReady] = useState<boolean>(false);
  const [sectionContent, setSectionContent] = useState<SectionContent | undefined>();
  const [isProjectSetupOpen, setIsProjectSetupOpen] = useState(false);
  const [projectSetupSection, setProjectSetupSection] = useState<'basic' | 'targets' | 'references' | 'encryption' | 'danger'>('basic');
  const [editingProject, setEditingProject] = useState<any | null>(null);
  const [isGenreSelectorOpen, setIsGenreSelectorOpen] = useState<boolean>(false);
  const [isLensPanelOpen, setIsLensPanelOpenState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem('wrlens:lensPanelOpen') === '1';
    } catch {
      return false;
    }
  });
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem('wrlens:aiAssistantOpen') === '1';
    } catch {
      return false;
    }
  });
  const [isAIConfigOpen, setIsAIConfigOpen] = useState<boolean>(false);
  // Revision bump para forzar re-renders dependientes de metadatos de proyecto (targetWords, etc.)
  const [projectRevision, setProjectRevision] = useState<number>(0);
  // Bump que se incrementa al completar un pull/sync — fuerza recarga del contenido activo desde IndexedDB.
  const [syncBump, setSyncBump] = useState<number>(0);

  const activeProject = useLiveQuery(() => {
    if (!activeProjectId) return undefined;
    return db.projects.get(activeProjectId);
  }, [activeProjectId]);

  const sections = useLiveQuery(() => {
    if (!activeProjectId || !activeVersionId) return [];
    return sectionsRepo.listByProjectVersion(activeProjectId, activeVersionId);
  }, [activeProjectId, activeVersionId], []);

  const versions = useLiveQuery(() => {
    if (!activeProjectId) return [];
    return db.writingVersions.where({ projectId: activeProjectId }).sortBy('createdAt');
  }, [activeProjectId], []);

  // Calcula la versión activa como objeto
  const activeVersion = React.useMemo(() => {
    return versions?.find((v) => v.id === activeVersionId);
  }, [versions, activeVersionId]);

  useEffect(() => {
    setActiveVersionId((current) => resolveNextActiveVersionId(versions ?? [], current));
  }, [versions]);

  useEffect(() => {
    try {
      localStorage.setItem('wrlens:lensPanelOpen', isLensPanelOpen ? '1' : '0');
    } catch {}
  }, [isLensPanelOpen]);

  useEffect(() => {
    try {
      localStorage.setItem('wrlens:aiAssistantOpen', isAIAssistantOpen ? '1' : '0');
    } catch {}
  }, [isAIAssistantOpen]);

  const handleSetIsLensPanelOpen = useCallback((open: boolean) => {
    setIsLensPanelOpenState(open);
    if (open) {
      setIsAIAssistantOpen(false);
    }
  }, []);

  const handleSetIsAIAssistantOpen = useCallback((open: boolean) => {
    setIsAIAssistantOpen(open);
    if (open) {
      setIsLensPanelOpenState(false);
    }
  }, []);

  const clearPendingProjectResume = useCallback(() => {
    setPendingProjectResume(null);
  }, []);

  const activateProject = useCallback((nextProjectId?: string, resumeRequest?: PendingProjectResumeRequest | null) => {
    if (activeProjectId && nextProjectId !== activeProjectId) {
      try {
        window.dispatchEvent(new CustomEvent('wrlens:resume:flush-request', {
          detail: {
            projectId: activeProjectId,
            nextProjectId: nextProjectId ?? null,
          },
        }));
      } catch {}
    }
    setPendingProjectResume(resumeRequest ?? null);
    setActiveProjectIdState(nextProjectId);
  }, [activeProjectId]);

  const handleSetActiveProjectId = useCallback((nextProjectId?: string) => {
    activateProject(nextProjectId, null);
  }, [activateProject]);

  const requestProjectResume = useCallback((projectId: string) => {
    const request: PendingProjectResumeRequest = {
      projectId,
      requestedAt: new Date().toISOString(),
    };
    setIsResumeFetching(true);

    const fetchAndActivate = async () => {
      try {
        const { supabase } = await import('@core/auth');
        if (supabase) {
          const abortController = new AbortController();
          const timer = window.setTimeout(() => abortController.abort(), 3000);
          try {
            const { data, error } = await (supabase
              .from('projects')
              .select('metadata')
              .eq('id', projectId)
              .single() as any).abortSignal(abortController.signal);
            window.clearTimeout(timer);
            if (!error && data) {
              const localProject = await projectsRepo.get(projectId);
              if (localProject) {
                const localCustomFields = (localProject.metadata?.customFields ?? {}) as Record<string, unknown>;
                const remoteCustomFields = ((data as any)?.metadata?.customFields ?? {}) as Record<string, unknown>;
                const localCheckpoint = sanitizeProjectResumeCheckpoint(localCustomFields[PROJECT_RESUME_CHECKPOINT_KEY]);
                const remoteCheckpoint = sanitizeProjectResumeCheckpoint(remoteCustomFields[PROJECT_RESUME_CHECKPOINT_KEY]);
                const remoteIsNewer = !!remoteCheckpoint && (
                  !localCheckpoint ||
                  Date.parse(remoteCheckpoint.updatedAt) > Date.parse(localCheckpoint.updatedAt)
                );
                if (remoteIsNewer) {
                  const merged = mergeCustomFieldsWithResumeCheckpoint(localCustomFields, remoteCustomFields);
                  await projectsRepo.update(projectId, {
                    metadata: { ...localProject.metadata, customFields: merged },
                  });

                  if (remoteCheckpoint.sectionId && remoteCheckpoint.versionId) {
                    try {
                      const { data: remoteContentRow, error: contentError } = await (supabase
                        .from('section_contents')
                        .select('id, section_id, version_id, content_text, content_cipher, content_hash, is_encrypted, created_at, updated_at, deleted_at')
                        .eq('section_id', remoteCheckpoint.sectionId)
                        .eq('version_id', remoteCheckpoint.versionId)
                        .is('deleted_at', null)
                        .order('updated_at', { ascending: false })
                        .limit(1)
                        .maybeSingle() as any).abortSignal(abortController.signal);

                      if (!contentError && remoteContentRow) {
                        const decoded = await decodeRemoteResumeSectionContent(projectId, remoteContentRow);
                        if (decoded) {
                          await upsertRemoteResumeSectionContent(remoteContentRow, decoded);
                        }
                      }
                    } catch {
                      // si el contenido remoto no está disponible o no se puede descifrar,
                      // continuamos con el checkpoint remoto y el contenido local actual.
                    }
                  }
                }
              }
            }
          } catch {
            window.clearTimeout(timer);
            // offline, timeout o error de auth → continuar con checkpoint local
          }
        }
      } catch {
        // supabase no disponible (offline-first sin config) → continuar con checkpoint local
      }
    };

    void fetchAndActivate().finally(() => {
      setIsResumeFetching(false);
      activateProject(projectId, request);
    });
  }, [activateProject]);

  // --- NUEVO: Cache de contenido por sección+versión ---
  const [contentCache, setContentCache] = useState<{ [key: string]: string }>({});
  const isDirtyRef = React.useRef(false);
  const pendingSaveRef = React.useRef<{ cacheKey: string; contentId: string; contentSectionId: string; value: string } | null>(null);
  const saveTimerRef = React.useRef<number | null>(null);
  const lastConfirmedByCacheKeyRef = React.useRef<Record<string, string>>({});
  const lastPersistedAtByCacheKeyRef = React.useRef<Record<string, number>>({});
  const contentCacheRef = React.useRef(contentCache);
  const activeSectionIdRef = React.useRef<string | undefined>(activeSection?.id);
  const activeVersionIdRef = React.useRef<string | undefined>(activeVersionId);
  const sectionContentRef = React.useRef<SectionContent | undefined>(sectionContent);
  contentCacheRef.current = contentCache;
  activeSectionIdRef.current = activeSection?.id;
  activeVersionIdRef.current = activeVersionId;
  sectionContentRef.current = sectionContent;
  const getCacheKey = (sectionId?: string, versionId?: string) =>
    sectionId && versionId ? `${sectionId}_${versionId}` : '';

  // --- Escucha eventos de sync completado y fuerza recarga de contenido desde IndexedDB ---
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { stage?: string } | undefined;
      const stage = detail?.stage ?? '';
      // Recargar tras pull completado (evita doble recarga por sync:done).
      if (stage === 'pull:done') {
        setSyncBump(n => n + 1);
      }
    };
    window.addEventListener('wrlens:sync-progress', handler);
    return () => window.removeEventListener('wrlens:sync-progress', handler);
  }, []);

  // --- Cargar contenido de sección+versión y sincronizar cache ---
  useEffect(() => {
    if (!activeSection?.id || !activeVersionId) return;
    const sectionId = activeSection.id;
    const versionId = activeVersionId;
    const cacheKey = getCacheKey(sectionId, versionId);
    let cancelled = false;

    sectionContentsRepo.get(sectionId, versionId).then(async existing => {
      if (cancelled) return;
      const content = existing ?? await sectionContentsRepo.getOrCreate(sectionId, versionId, () => ({
        id: uuidv4(),
        sectionId,
        versionId,
        content: '',
        createdAt: new Date(),
      }));
      if (cancelled) return;

      const remoteValue = content?.content ?? '';
      const localValue = contentCacheRef.current[cacheKey];
      const hasLocalEdits = isDirtyRef.current && localValue !== undefined;

      setSectionContent(content);
      sectionContentRef.current = content;
      if (!hasLocalEdits) {
        contentCacheRef.current = { ...contentCacheRef.current, [cacheKey]: remoteValue };
        setContentCache(prev => ({ ...prev, [cacheKey]: remoteValue }));
        setEditorContentState(remoteValue);
        lastConfirmedByCacheKeyRef.current[cacheKey] = remoteValue;
        isDirtyRef.current = false;
      }
      setIsSectionContentReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [activeSection?.id, activeVersionId, syncBump]);

  useEffect(() => {
    setIsSectionContentReady(false);
  }, [activeSection?.id, activeVersionId]);

  const persistPendingSave = useCallback(async (pending: { cacheKey: string; contentId: string; contentSectionId: string; value: string } | null) => {
    if (!pending) return;
    await sectionContentsRepo.updateContent(pending.contentId, pending.value);
    console.log(`✅ Contenido guardado para sección ${pending.contentSectionId}`);
    setSectionContent(prev => (prev && prev.id === pending.contentId ? { ...prev, content: pending.value } : prev));
    lastConfirmedByCacheKeyRef.current[pending.cacheKey] = pending.value;
    lastPersistedAtByCacheKeyRef.current[pending.cacheKey] = Date.now();
    if ((contentCacheRef.current[pending.cacheKey] ?? '') === pending.value) {
      isDirtyRef.current = false;
    }
    if (
      pendingSaveRef.current &&
      pendingSaveRef.current.cacheKey === pending.cacheKey &&
      pendingSaveRef.current.contentId === pending.contentId &&
      pendingSaveRef.current.value === pending.value
    ) {
      pendingSaveRef.current = null;
    }
    saveTimerRef.current = null;
  }, []);

  const flushPendingSave = useCallback(async () => {
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingSaveRef.current;
    if (!pending) return;
    pendingSaveRef.current = null;
    await persistPendingSave(pending).catch((error) => {
      console.warn('[WriterProvider] guardado local falló:', error);
    });
  }, [persistPendingSave]);

  // --- Guardado local: debounce corto + throttle + dedupe ---
  useEffect(() => {
    const currentSectionContent = sectionContentRef.current;
    if (!isDirtyRef.current || !currentSectionContent) return;
    const cacheKey = getCacheKey(activeSectionIdRef.current, activeVersionIdRef.current);
    if (!cacheKey) return;
    const value = contentCache[cacheKey] ?? '';
    if (value === currentSectionContent.content || value === lastConfirmedByCacheKeyRef.current[cacheKey]) {
      isDirtyRef.current = false;
      return;
    }

    if (
      pendingSaveRef.current &&
      pendingSaveRef.current.cacheKey === cacheKey &&
      pendingSaveRef.current.value === value
    ) {
      return;
    }

    const nextPending = {
      cacheKey,
      contentId: currentSectionContent.id,
      contentSectionId: currentSectionContent.sectionId,
      value,
    };
    pendingSaveRef.current = nextPending;

    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    const elapsed = Date.now() - (lastPersistedAtByCacheKeyRef.current[cacheKey] ?? 0);
    const throttleWait = Math.max(0, LOCAL_SAVE_THROTTLE_MS - elapsed);
    const delay = Math.max(LOCAL_SAVE_DEBOUNCE_MS, throttleWait);

    saveTimerRef.current = window.setTimeout(() => {
      void persistPendingSave(pendingSaveRef.current).catch((error) => {
        console.warn('[WriterProvider] guardado local falló:', error);
      });
    }, delay);
  }, [contentCache, persistPendingSave]);

  useEffect(() => () => {
    void flushPendingSave();
  }, [flushPendingSave]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void flushPendingSave();
      }
    };
    const onBlur = () => { void flushPendingSave(); };
    const onSyncProgress = (e: Event) => {
      const stage = (e as CustomEvent).detail?.stage;
      if (stage === 'sync:start') {
        void flushPendingSave();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('wrlens:sync-progress', onSyncProgress);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('wrlens:sync-progress', onSyncProgress);
    };
  }, [flushPendingSave]);

  useEffect(() => {
    return () => {
      void flushPendingSave();
    };
  }, [activeSection?.id, activeVersionId, flushPendingSave]);

  // --- Actualizar cache y editorContent al escribir ---
  const handleEditorChange = (value: string) => {
    if (!activeSection?.id || !activeVersionId) return;
    const cacheKey = getCacheKey(activeSection.id, activeVersionId);
    if ((contentCacheRef.current[cacheKey] ?? '') === value) return;
    isDirtyRef.current = true;
    contentCacheRef.current = { ...contentCacheRef.current, [cacheKey]: value };
    setEditorContentState(value);
    setContentCache((prev) => {
      if ((prev[cacheKey] ?? '') === value) return prev;
      return { ...prev, [cacheKey]: value };
    });
  };

  const ensureSectionContentLoaded = useCallback(async (
    sectionId: string,
    versionId: string,
    options?: { reason?: string; hydrateEditor?: boolean },
  ): Promise<SectionContent | undefined> => {
    const current = sectionContentRef.current;
    if (current && current.sectionId === sectionId && current.versionId === versionId) {
      return current;
    }

    const cacheKey = getCacheKey(sectionId, versionId);
    let content = await sectionContentsRepo.get(sectionId, versionId);
    if (!content) {
      content = await sectionContentsRepo.getOrCreate(sectionId, versionId, () => ({
        id: uuidv4(),
        sectionId,
        versionId,
        content: '',
        createdAt: new Date(),
      }));
    }

    if (import.meta.env.DEV) {
      console.debug('[figureFlow] content_bootstrap', {
        reason: options?.reason ?? 'unknown',
        sectionId,
        versionId,
        contentId: content.id,
        length: content.content?.length ?? 0,
      });
    }

    contentCacheRef.current = { ...contentCacheRef.current, [cacheKey]: content?.content ?? '' };
    setContentCache((prev) => {
      if ((prev[cacheKey] ?? '') === (content?.content ?? '')) return prev;
      return { ...prev, [cacheKey]: content?.content ?? '' };
    });
    lastConfirmedByCacheKeyRef.current[cacheKey] = content.content ?? '';
    const shouldHydrateEditor = options?.hydrateEditor ?? (activeSectionIdRef.current === sectionId && activeVersionIdRef.current === versionId);
    if (shouldHydrateEditor) {
      setSectionContent(content);
      sectionContentRef.current = content;
      setEditorContentState(content.content ?? '');
      setIsSectionContentReady(true);
    }
    return content;
  }, []);

  const transformSectionContent = useCallback(async (
    sectionId: string,
    versionId: string,
    transformer: (current: string) => string,
    options?: { reason?: string; persistImmediately?: boolean },
  ): Promise<string | undefined> => {
    if (!sectionId || !versionId) return undefined;
    const isActiveTarget = activeSectionIdRef.current === sectionId && activeVersionIdRef.current === versionId;
    const currentSectionContent = await ensureSectionContentLoaded(sectionId, versionId, {
      reason: options?.reason,
      hydrateEditor: isActiveTarget,
    });
    if (!currentSectionContent) return undefined;

    const cacheKey = getCacheKey(sectionId, versionId);
    const currentValue = contentCacheRef.current[cacheKey] ?? currentSectionContent.content ?? '';
    const nextValue = transformer(currentValue);

    if (nextValue === currentValue) {
      if (import.meta.env.DEV && options?.reason) {
        console.debug('[writerFlow] transform skipped (no content change)', { reason: options.reason, sectionId, versionId });
      }
      return currentValue;
    }

    contentCacheRef.current = { ...contentCacheRef.current, [cacheKey]: nextValue };
    setContentCache((prev) => {
      if ((prev[cacheKey] ?? '') === nextValue) return prev;
      return { ...prev, [cacheKey]: nextValue };
    });

    if (isActiveTarget) {
      isDirtyRef.current = true;
      setEditorContentState(nextValue);
    }

    if (options?.persistImmediately ?? true) {
      const pending = {
        cacheKey,
        contentId: currentSectionContent.id,
        contentSectionId: currentSectionContent.sectionId,
        value: nextValue,
      };
      try {
        await persistPendingSave(pending);
      } catch (error) {
        console.warn('[writerFlow] transform persist failed', {
          reason: options?.reason ?? 'unknown',
          sectionId,
          versionId,
          error,
        });
      }
    }

    return nextValue;
  }, [ensureSectionContentLoaded, persistPendingSave]);

  const transformEditorContent = useCallback(async (
    transformer: (current: string) => string,
    options?: { reason?: string; persistImmediately?: boolean },
  ): Promise<string | undefined> => {
    const sectionId = activeSectionIdRef.current;
    const versionId = activeVersionIdRef.current;
    if (!sectionId || !versionId) return undefined;
    const currentSectionContent = await ensureSectionContentLoaded(sectionId, versionId, {
      reason: options?.reason,
      hydrateEditor: true,
    });
    if (!currentSectionContent) return undefined;

    const cacheKey = getCacheKey(sectionId, versionId);
    const currentValue = contentCacheRef.current[cacheKey] ?? currentSectionContent.content ?? '';
    const nextValue = transformer(currentValue);

    if (nextValue === currentValue) {
      if (import.meta.env.DEV && options?.reason) {
        console.debug('[figureFlow] transform skipped (no content change)', { reason: options.reason, sectionId, versionId });
      }
      return currentValue;
    }

    if (import.meta.env.DEV) {
      console.debug('[figureFlow] transform start', {
        reason: options?.reason ?? 'unknown',
        sectionId,
        versionId,
        beforeLength: currentValue.length,
        afterLength: nextValue.length,
      });
    }

    isDirtyRef.current = true;
    contentCacheRef.current = { ...contentCacheRef.current, [cacheKey]: nextValue };
    setEditorContentState(nextValue);
    setContentCache((prev) => {
      if ((prev[cacheKey] ?? '') === nextValue) return prev;
      return { ...prev, [cacheKey]: nextValue };
    });

    if (options?.persistImmediately ?? true) {
      const pending = {
        cacheKey,
        contentId: currentSectionContent.id,
        contentSectionId: currentSectionContent.sectionId,
        value: nextValue,
      };
      pendingSaveRef.current = pending;
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      try {
        await persistPendingSave(pending);
        if (import.meta.env.DEV) {
          console.debug('[figureFlow] transform persisted', {
            reason: options?.reason ?? 'unknown',
            sectionId,
            versionId,
            persistedLength: nextValue.length,
          });
        }
      } catch (error) {
        console.warn('[figureFlow] transform persist failed', {
          reason: options?.reason ?? 'unknown',
          sectionId,
          versionId,
          error,
        });
      }
    }

    return nextValue;
  }, [ensureSectionContentLoaded, persistPendingSave]);

  const createNewProject = useCallback(async (data: any): Promise<string> => {
    try {
      const projectId = uuidv4();
      await projectsRepo.create({
        id: projectId,
        name: data.metadata.title,
        genreId: data.genre.id,
        metadata: { ...data.metadata, language: data.metadata.language || 'es' },
        coverImage: data.coverImage,
      });

      const versionId = uuidv4();
      await writingVersionsRepo.create({
        id: versionId,
        projectId,
        name: 'v1',
        createdAt: new Date(),
        isFinal: false,
      });

      // LOG: Estructura del género
      console.log('[WriterProvider] Estructura del género:', data.genre.structure);
      if (!Array.isArray(data.genre.structure) || data.genre.structure.length === 0) {
        console.warn('[WriterProvider] ⚠️ El género seleccionado no tiene estructura definida. No se crearán secciones.');
      }

  const clientId = await getDeviceIdLazy();

      const sectionPromises = (data.genre.structure || []).map((name: string, index: number) => {
        const sectionId = uuidv4();
        console.log(`[WriterProvider] Creando sección: ${name} (orden ${index}) para proyecto ${projectId} y versión ${versionId}`);
        return sectionsRepo.create({
          id: sectionId,
          projectId,
          versionId,
          title: name,
          order: index,
          level: 0,
          status: 'todo',
          clientId,
        });
      });
      await Promise.all(sectionPromises);

      // LOG: Secciones creadas
      const newSections = await sectionsRepo.listByProjectVersion(projectId, versionId);
      console.log('[WriterProvider] Secciones creadas:', newSections);
      for (const section of newSections) {
        await sectionContentsRepo.getOrCreate(section.id, versionId, () => ({
          id: uuidv4(),
          sectionId: section.id,
          versionId,
          content: '',
          createdAt: new Date(),
        }));
      }

      setActiveProjectIdState(projectId);
      setSelectedGenre(data.genre);
      setIsProjectSetupOpen(false);
      // Seleccionar automáticamente la primera sección como activa
      if (newSections.length > 0) {
        console.log('[setActiveSection] Seleccionando primera sección tras crear proyecto:', newSections[0]);
        setActiveSection(newSections[0]);
      }
      
      return projectId;
    } catch (err) {
      console.error('❌ Error creando proyecto:', err);
      throw err; // Re-throw to maintain the Promise rejection
    }
  }, []);

  // Al crear una subsección, seleccionarla automáticamente
  const createSection = useCallback(async ({ title, parentId, selectAfterCreate = true }: { title: string; parentId?: string | null; selectAfterCreate?: boolean; }) => {
    if (!activeProjectId || !activeVersionId) return undefined;
    const normalizedParentId = parentId ?? undefined;
    const parentSection = normalizedParentId ? await sectionsRepo.get(normalizedParentId) : undefined;
    const siblingCount = await db.sections
      .where({ projectId: activeProjectId, versionId: activeVersionId })
      .filter((s) => !s.deletedAt && (s.parentSectionId ?? undefined) === normalizedParentId)
      .count();
    const sectionId = uuidv4();
    await sectionsRepo.create({
      id: sectionId,
      projectId: activeProjectId,
      versionId: activeVersionId,
      title,
      order: siblingCount,
      parentSectionId: normalizedParentId ?? null,
      level: parentSection ? (parentSection.level ?? 0) + 1 : 0,
      status: parentSection ? 'draft' : 'todo',
  clientId: await getDeviceIdLazy(),
    });
    await sectionContentsRepo.getOrCreate(sectionId, activeVersionId, () => ({
      id: uuidv4(),
      sectionId,
      versionId: activeVersionId,
      content: '',
      createdAt: new Date(),
    }));
    if (selectAfterCreate) {
      const newSection = await sectionsRepo.get(sectionId);
      if (newSection) {
        setActiveSection(newSection);
      }
    }
    return sectionId;
  }, [activeProjectId, activeVersionId, setActiveSection]);

  const createSubsection = useCallback(async (parentId: string, title: string) => {
    await createSection({ title, parentId });
  }, [createSection]);

  const applyOutlineReorderAction = useCallback(async (action: OutlineReorderAction) => {
    if (!activeProjectId || !activeVersionId) return;
    await db.transaction('rw', db.sections, async () => {
      const rows = (await db.sections.where({ projectId: activeProjectId, versionId: activeVersionId }).toArray())
        .filter((row: any) => !row.deletedAt);
      const normalized: Section[] = rows.map((row: any) => {
        const title = row?.title ?? row?.name ?? 'Sección';
        return {
          id: row.id,
          projectId: row.projectId,
          versionId: row.versionId,
          title,
          name: row?.name ?? title,
          order: row?.order ?? 0,
          level: row?.level ?? 0,
          parentSectionId: row?.parentSectionId ?? undefined,
          status: (row?.status as SectionStatus) ?? 'todo',
          planningState: (row?.planningState as SectionPlanningState) ?? 'normal',
          labels: Array.isArray(row?.labels) ? row.labels : [],
          hidden: row?.hidden ?? false,
          dueDate: row?.dueDate,
          updatedAt: row?.updatedAt ?? new Date().toISOString(),
          clientId: row?.clientId,
          focusPinned: row?.focusPinned ?? false,
        };
      });

      const result = computeOutlineReorder(normalized, action);
      if (!result.changed) return;

      const nowIso = new Date().toISOString();
      for (const update of result.updates) {
        await db.sections.update(update.id, {
          parentSectionId: update.changes.parentSectionId ?? undefined,
          order: update.changes.order,
          level: update.changes.level,
          updatedAt: nowIso,
          dirty: true,
        });
      }
    });
  }, [activeProjectId, activeVersionId]);

  const renameSection = useCallback(async (sectionId: string, title: string) => {
    await sectionsRepo.update(sectionId, { title, name: title });
    const updated = await sectionsRepo.get(sectionId);
    if (updated && activeSection?.id === sectionId) {
      setActiveSection(updated);
    }
    if (activeProjectId && activeVersionId) {
      await outlineOpsRepo.enqueue({
        id: uuidv4(),
        projectId: activeProjectId,
        versionId: activeVersionId,
        sectionId,
        type: 'section:update',
        payload: { title },
        createdAt: new Date().toISOString(),
  clientId: await getDeviceIdLazy(),
        syncState: 'pending',
      });
    }
  }, [activeSection?.id, activeProjectId, activeVersionId, setActiveSection]);

  const deleteSection = useCallback(async function deleteSectionRecursive(sectionId: string) {
    const children = (await db.sections.where({ parentSectionId: sectionId }).toArray())
      .filter((row: any) => !row.deletedAt);
    for (const child of children) {
      await deleteSectionRecursive(child.id);
    }
    await sectionsRepo.delete(sectionId);
    const nowIso = new Date().toISOString();
    const sectionContents = await db.sectionContents.where({ sectionId }).toArray();
    for (const row of sectionContents) {
      await db.sectionContents.update(row.id, {
        deletedAt: nowIso,
        dirty: true,
        updatedAt: nowIso,
      } as any);
    }
    if (activeSection?.id === sectionId) {
      setActiveSection(undefined);
    }
  }, [activeSection?.id, setActiveSection]);

  const moveSectionUp = useCallback(async (sectionId: string) => {
    await applyOutlineReorderAction({ type: 'move-up', sectionId });
  }, [applyOutlineReorderAction]);

  const moveSectionDown = useCallback(async (sectionId: string) => {
    await applyOutlineReorderAction({ type: 'move-down', sectionId });
  }, [applyOutlineReorderAction]);

  const indentSection = useCallback(async (sectionId: string) => {
    await applyOutlineReorderAction({ type: 'indent', sectionId });
  }, [applyOutlineReorderAction]);

  const outdentSection = useCallback(async (sectionId: string) => {
    await applyOutlineReorderAction({ type: 'outdent', sectionId });
  }, [applyOutlineReorderAction]);

  const moveSection = useCallback(async (sectionId: string, target: { parentSectionId?: string | null; order: number }) => {
    if (!activeProjectId || !activeVersionId) return;
    const list = await sectionsRepo.listByProjectVersion(activeProjectId, activeVersionId);
    const byId = new Map(list.map((s) => [s.id, s] as const));
    const source = byId.get(sectionId);
    if (!source) return;

    const desiredParentId = target.parentSectionId ?? undefined;
    const previousParentId = source.parentSectionId ?? undefined;
    const parent = desiredParentId ? byId.get(desiredParentId) : undefined;
    const newLevel = parent ? (parent.level ?? 0) + 1 : 0;

    const siblings = list
      .filter((s) => (s.parentSectionId ?? undefined) === desiredParentId && s.id !== sectionId)
      .sort((a, b) => a.order - b.order);

    const insertIndex = Math.min(Math.max(target.order, 0), siblings.length);
    siblings.splice(insertIndex, 0, { ...source, parentSectionId: desiredParentId, level: newLevel });

    const updates: Array<{ id: string; changes: Partial<Section> }> = [];
    siblings.forEach((sibling, index) => {
      const isSource = sibling.id === sectionId;
      updates.push({
        id: sibling.id,
        changes: {
          order: index,
          parentSectionId: isSource ? desiredParentId : sibling.parentSectionId,
          level: isSource ? newLevel : sibling.level,
        },
      });
    });

    const childrenMap = new Map<string, Section[]>();
    for (const section of list) {
      if (!section.parentSectionId) continue;
      const arr = childrenMap.get(section.parentSectionId) || [];
      arr.push(section);
      childrenMap.set(section.parentSectionId, arr);
    }

    const descendants: Section[] = [];
    const stack = [...(childrenMap.get(sectionId) || [])];
    while (stack.length) {
      const node = stack.pop()!;
      descendants.push(node);
      const next = childrenMap.get(node.id);
      if (next) stack.push(...next);
    }

    const levelDelta = newLevel - source.level;
    if (levelDelta !== 0) {
      for (const descendant of descendants) {
        updates.push({
          id: descendant.id,
          changes: { level: Math.max(0, descendant.level + levelDelta) },
        });
      }
    }

    if (previousParentId !== desiredParentId) {
      const previousSiblings = list
        .filter((s) => (s.parentSectionId ?? undefined) === previousParentId && s.id !== sectionId)
        .sort((a, b) => a.order - b.order);
      previousSiblings.forEach((sibling, index) => {
        updates.push({
          id: sibling.id,
          changes: { order: index },
        });
      });
    }

    await sectionsRepo.bulkUpdate(updates);
  }, [activeProjectId, activeVersionId]);

  const updateSectionStatus = useCallback(async (sectionId: string, status: SectionStatus) => {
    await sectionsRepo.update(sectionId, { status });
  }, []);

  const updateSectionPlanningState = useCallback(async (sectionId: string, planningState: SectionPlanningState) => {
    await sectionsRepo.update(sectionId, { planningState });
    if (activeSection?.id === sectionId) {
      const updated = await sectionsRepo.get(sectionId);
      if (updated) setActiveSection(updated);
    }
  }, [activeSection?.id, setActiveSection]);

  const updateSectionLabels = useCallback(async (sectionId: string, labels: string[]) => {
    await sectionsRepo.update(sectionId, { labels });
  }, []);

  const updateSectionDueDate = useCallback(async (sectionId: string, dueDate?: string) => {
    await sectionsRepo.update(sectionId, { dueDate });
  }, []);

  const toggleSectionHidden = useCallback(async (sectionId: string, hidden: boolean) => {
    await sectionsRepo.update(sectionId, { hidden });
  }, []);

  const bulkUpdateSections = useCallback(async (updates: Array<{ id: string; changes: Partial<Section> }>) => {
    await sectionsRepo.bulkUpdate(updates);
  }, []);

  // Sincroniza el género del proyecto activo con selectedGenre
  useEffect(() => {
    if (activeProject && (!selectedGenre || selectedGenre.id !== activeProject.genreId)) {
      const genre = genres.find((g) => g.id === activeProject.genreId);
      if (genre) {
        setSelectedGenre(genre);
        console.log('[WriterProvider] Género sincronizado automáticamente:', genre);
      } else {
        setSelectedGenre(null);
        console.warn('[WriterProvider] No se encontró el género para el proyecto activo');
      }
    }
  }, [activeProject, selectedGenre]);

  useEffect(() => {
    if (activeProjectId) return;
    setIsLensPanelOpenState(false);
    setIsAIAssistantOpen(false);
  }, [activeProjectId]);

  const contextValue: WriterContextType = {
    selectedGenre,
    setSelectedGenre,
    isGenreSelectorOpen,
    setIsGenreSelectorOpen,
    isLensPanelOpen,
    setIsLensPanelOpen: handleSetIsLensPanelOpen,
    isAIAssistantOpen,
    setIsAIAssistantOpen: handleSetIsAIAssistantOpen,
    isAIConfigOpen,
    setIsAIConfigOpen,
    activeProjectId,
    setActiveProjectId: handleSetActiveProjectId,
    pendingProjectResume,
    isResumeFetching,
    requestProjectResume,
    clearPendingProjectResume,
    activeProject,
    sections,
    activeSection,
    setActiveSection,
    activeVersionId,
    setActiveVersionId,
    editorContent,
    setEditorContent: handleEditorChange,
    isSectionContentReady,
    transformEditorContent,
    transformSectionContent,
    flushPendingSave,
    isProjectSetupOpen,
    setIsProjectSetupOpen,
    projectSetupSection,
    setProjectSetupSection,
    createNewProject,
    createSubsection,
    createSection,
    renameSection,
    deleteSection,
    moveSectionUp,
    moveSectionDown,
    indentSection,
    outdentSection,
    moveSection,
    updateSectionStatus,
    updateSectionPlanningState,
    updateSectionLabels,
    updateSectionDueDate,
    toggleSectionHidden,
    bulkUpdateSections,
    editingProject,
    setEditingProject,
    activeVersion, // <-- Añadido para exponer el objeto de versión activa
    // Exposición interna opcional (no tipado original): projectRevision & bump
    // @ts-ignore - extendemos incrementalmente el contexto
    projectRevision,
    // @ts-ignore
    bumpProjectRevision: () => setProjectRevision(r => r + 1),
  };

  return (
    <WriterContext.Provider value={contextValue}>
      {children}
    </WriterContext.Provider>
  );
};
